/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * DATA ACCESS LAYER : Virality Expert (VE) Actions
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Gère les actions spécifiques au VE : upload de fichiers Derush,
 * validation pour passer au montage, et validation du montage pour le client.
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { supabase } from "@/integrations/supabase/client";
import { findShootingFolder, createVirtualFolder, createVirtualFile, getVirtualFolders, extractIdFromFolderName, buildCustomerFolderNameWithId, buildTopicFolderNameWithId } from "./rushesVirtualFolders";
import { uploadFileToS3 } from "./s3Files";

/**
 * Vérifie si le dossier Derush d'une vidéo contient des fichiers
 */
export async function hasDerushFiles(
  shootingId: string,
  videoContentId: string
): Promise<boolean> {
  try {
    const shootingFolder = await findShootingFolder(shootingId);
    if (!shootingFolder) return false;

    const { data: vc } = await supabase
      .from("video_content")
      .select("title")
      .eq("id", videoContentId)
      .single();

    if (!vc?.title) return false;

    const subfolders = await getVirtualFolders(shootingFolder.id);
    
    // Match by embedded video content ID (new format) or by title (legacy)
    const shortVcId = videoContentId.replaceAll('-', '').slice(0, 8).toLowerCase();
    const topicFolder = subfolders.find(f => {
      const embedded = extractIdFromFolderName(f.name);
      if (embedded && embedded.toLowerCase() === shortVcId) return true;
      return f.name === vc.title;
    });
    if (!topicFolder) return false;

    const topicSubfolders = await getVirtualFolders(topicFolder.id);
    const derushFolder = topicSubfolders.find(f => f.name === 'Derush');
    if (!derushFolder) return false;

    const { count } = await supabase
      .from("rushes_virtual_files")
      .select("id", { count: 'exact', head: true })
      .eq("folder_id", derushFolder.id);

    return (count ?? 0) > 0;
  } catch {
    return false;
  }
}

/**
 * Builds a customer folder name from user data
 */
function buildCustomerFolderName(user: { first_name?: string; last_name?: string; email: string }): string {
  const firstName = user.first_name?.trim() || '';
  const lastName = user.last_name?.trim() || '';

  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;
  return user.email.split('@')[0];
}

/**
 * Builds a customer folder name with embedded client ID (new format)
 */
function buildCustomerFolderNameNew(user: { first_name?: string; last_name?: string; email: string }, userId: string): string {
  const displayName = buildCustomerFolderName(user);
  return buildCustomerFolderNameWithId(displayName, userId);
}

/**
 * Finds or creates a customer root folder
 */
async function findOrCreateCustomerFolder(customerFolderName: string, userId?: string): Promise<string | null> {
  // Strategy 1: Try to find by embedded client ID (new format)
  if (userId) {
    const { data: allRootFolders } = await supabase
      .from("rushes_virtual_folders")
      .select("id, name")
      .is("parent_folder_id", null);

    if (allRootFolders) {
      const shortId = userId.replaceAll('-', '').slice(0, 8).toLowerCase();
      const match = allRootFolders.find(f => {
        const embedded = extractIdFromFolderName(f.name);
        return embedded && embedded.toLowerCase() === shortId;
      });
      if (match) return match.id;
    }
  }

  // Strategy 2: Legacy name match (case-insensitive)
  const { data: customerFolders } = await supabase
    .from("rushes_virtual_folders")
    .select("id, name")
    .is("parent_folder_id", null)
    .ilike("name", customerFolderName);

  if (customerFolders && customerFolders.length > 0) {
    return customerFolders[0].id;
  }

  // Create with new format if userId is available
  const folderNameToCreate = userId
    ? buildCustomerFolderNameWithId(customerFolderName, userId)
    : customerFolderName;

  const result = await createVirtualFolder(folderNameToCreate, null);
  return result.success && result.folder ? result.folder.id : null;
}

/**
 * Creates a shooting folder hierarchy when it doesn't exist
 */
async function createShootingFolderHierarchy(shootingId: string): Promise<{ id: string; name: string; parent_folder_id: string | null; created_by: string | null; created_at: string; updated_at: string } | null> {
  console.warn("[veActions] Shooting folder not found, attempting to create it");

  const { data: shooting } = await supabase
    .from("shootings")
    .select("user_id, users!inner(first_name, last_name, email)")
    .eq("id", shootingId)
    .single();

  if (!shooting) return null;

  const user = shooting.users as { first_name?: string; last_name?: string; email: string };
  const customerFolderName = buildCustomerFolderName(user);
  const customerFolderId = await findOrCreateCustomerFolder(customerFolderName, shooting.user_id);

  if (!customerFolderId) return null;

  const { data: shootings } = await supabase
    .from("shootings")
    .select("id, date")
    .eq("user_id", shooting.user_id)
    .order("date", { ascending: true });

  const shootingIndex = (shootings || []).findIndex(s => s.id === shootingId) + 1;
  if (shootingIndex === 0) return null;

  const folderResult = await createVirtualFolder(String(shootingIndex), customerFolderId);
  return folderResult.success && folderResult.folder ? folderResult.folder : null;
}

/**
 * Finds or creates a topic folder within a shooting folder
 */
async function findOrCreateTopicFolder(
  shootingFolderId: string,
  videoContentId: string
): Promise<{ id: string; name: string } | null> {
  const { data: vc } = await supabase
    .from("video_content")
    .select("title")
    .eq("id", videoContentId)
    .single();

  const subfolders = await getVirtualFolders(shootingFolderId);
  
  // Strategy 1: Match by embedded video content ID (new format)
  const shortVcId = videoContentId.replaceAll('-', '').slice(0, 8).toLowerCase();
  const matchById = subfolders.find(f => {
    const embedded = extractIdFromFolderName(f.name);
    return embedded && embedded.toLowerCase() === shortVcId;
  });
  if (matchById) return matchById;
  
  // Strategy 2: Match by exact title (legacy format)
  const existingFolder = subfolders.find(f => f.name === vc?.title);
  if (existingFolder) return existingFolder;

  // Create with new format (embedded ID)
  const folderName = vc?.title || `Video ${videoContentId.slice(0, 8)}`;
  const newName = buildTopicFolderNameWithId(folderName, videoContentId);
  const result = await createVirtualFolder(newName, shootingFolderId);

  return result.success && result.folder ? result.folder : null;
}

/**
 * Ensures Derush and Montage subfolders exist within a topic folder
 */
async function ensureTopicSubfolders(topicFolderId: string): Promise<string | null> {
  const topicSubfolders = await getVirtualFolders(topicFolderId);

  // Ensure Montage subfolder exists
  if (!topicSubfolders.some(f => f.name === 'Montage')) {
    await createVirtualFolder('Montage', topicFolderId);
  }

  // Find or create Derush subfolder
  const derushFolder = topicSubfolders.find(f => f.name === 'Derush');
  if (derushFolder) return derushFolder.id;

  const result = await createVirtualFolder('Derush', topicFolderId);
  return result.success && result.folder ? result.folder.id : null;
}

/**
 * Trouve le dossier shooting ou le crée s'il n'existe pas,
 * puis trouve ou crée le sous-dossier Derush du dossier sujet d'une vidéo
 */
async function findOrCreateDerushFolder(shootingId: string, videoContentId: string): Promise<string | null> {
  let shootingFolder = await findShootingFolder(shootingId);

  if (!shootingFolder) {
    shootingFolder = await createShootingFolderHierarchy(shootingId);
    if (!shootingFolder) return null;
  }

  const topicFolder = await findOrCreateTopicFolder(shootingFolder.id, videoContentId);
  if (!topicFolder) return null;

  return ensureTopicSubfolders(topicFolder.id);
}

/**
 * Upload un fichier source vers le dossier Derush d'une vidéo
 */
export async function uploadDerushFile(
  shootingId: string,
  videoContentId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; error?: string }> {
  try {
    const folderId = await findOrCreateDerushFolder(shootingId, videoContentId);
    if (!folderId) {
      return { success: false, error: "Impossible de trouver/créer le dossier Derush" };
    }

    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    const s3Key = `derush/${videoContentId}/${timestamp}-${randomId}/${file.name}`;

    const uploadResult = await uploadFileToS3(file, s3Key, onProgress);
    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error || "Échec de l'upload" };
    }

    await createVirtualFile(
      s3Key,
      file.name,
      folderId,
      file.size,
      new Date().toISOString()
    );

    return { success: true };
  } catch (err) {
    console.error("[veActions] uploadDerushFile error:", err);
    return { success: false, error: err instanceof Error ? err.message : "Erreur inconnue" };
  }
}

/**
 * Avance une vidéo du workflow VE vers l'étape suivante
 * - shot/derush_pending/derush_done → editing_pending (prêt pour monteur)
 * - editing_review → client_review (montage validé par VE)
 */
export async function advanceVEStatus(
  videoContentId: string
): Promise<{ success: boolean; newStatus?: string; error?: string }> {
  try {
    const { data: video, error: fetchError } = await supabase
      .from("video_content")
      .select("status")
      .eq("id", videoContentId)
      .single();

    if (fetchError || !video) {
      return { success: false, error: "Vidéo introuvable" };
    }

    let newStatus: string;
    const status = video.status as string;

    if (['shot', 'derush_pending', 'derush_done'].includes(status)) {
      newStatus = 'editing_pending';
    } else if (status === 'editing_review' || status === 'redelivery_review') {
      newStatus = 'client_review';
    } else {
      return { success: false, error: `Statut "${status}" ne peut pas être avancé par le VE` };
    }

    const { error: updateError } = await supabase
      .from("video_content")
      .update({ status: newStatus } as any)
      .eq("id", videoContentId);

    if (updateError) {
      return { success: false, error: updateError.message };
    }

    return { success: true, newStatus };
  } catch (err) {
    console.error("[veActions] advanceVEStatus error:", err);
    return { success: false, error: err instanceof Error ? err.message : "Erreur inconnue" };
  }
}
